import { useMemo } from 'react';

import {
  calculateThumbnails,
  ThumbnailMetadata,
  VideoMetadata,
  getTotalContentWidth,
} from '../utils/videoUtils';

interface UseVideoThumbnailsProps {
  videoMetadata: VideoMetadata | null | undefined;
  stripHeight: number;
  thumbnailsPerSecond?: number;
}

interface UseVideoThumbnailsReturn {
  thumbnails: ThumbnailMetadata[];
  isReady: boolean;
  totalContentWidth: number;
  totalThumbnails: number;
}

export function useVideoThumbnails({
  videoMetadata,
  stripHeight,
  thumbnailsPerSecond = 0.5,
}: UseVideoThumbnailsProps): UseVideoThumbnailsReturn {
  const thumbnails = useMemo(() => {
    if (!videoMetadata || stripHeight <= 0) {
      return [];
    }

    return calculateThumbnails(videoMetadata, stripHeight, thumbnailsPerSecond);
  }, [videoMetadata, stripHeight, thumbnailsPerSecond]);

  const isReady = useMemo(() => {
    return Boolean(videoMetadata && stripHeight > 0 && thumbnails.length > 0);
  }, [videoMetadata, stripHeight, thumbnails.length]);

  const totalContentWidth = useMemo(() => {
    return getTotalContentWidth(thumbnails);
  }, [thumbnails]);

  const totalThumbnails = thumbnails.length;

  return {
    thumbnails,
    isReady,
    totalContentWidth,
    totalThumbnails,
  };
}
